import type { WindingType } from "@/types/configurator";
import { formatGaugeRange } from "@/lib/catalog/shopify-catalog";
import {
  CONSTRUCTION_SUFFIX,
  constructionForGauges,
  presetForGauges,
  usesGaugeRange,
  windingForPosition,
  type StringConstruction,
} from "@/lib/configurator/preset-sets";

// Single source of truth for turning gauge numbers into beginner-friendly,
// consistent copy across product cards, product detail, and the configurator.
//
// Gauge = string thickness in inches. Each value maps to exactly one string.
// Bigger number = thicker string = more tension.
//
// String COUNT is a separate fact from gauge and is never derived from one —
// see stringCountLabel and StringProductMeta.stringCount.

export { formatGaugeRange, usesGaugeRange };

/**
 * Normalize any gauge input to inch form ".009".
 * Accepts inch decimals ("0.009", 0.042) and thousandths ("9", "09", "42")
 * as they appear in pipe-separated catalog metafields.
 *
 * Fractional thousandths are preserved: "10.5" → ".0105".
 */
export function formatGauge(value: string | number): string {
  const n =
    typeof value === "number"
      ? value
      : parseFloat(String(value).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return "";
  // Values >= 1 are thousandths-of-an-inch shorthand (e.g. 42 → .042).
  const inches = n >= 1 ? n / 1000 : n;
  // Keep a half-thousandth when the catalog carries one (.0105), otherwise
  // stay at the usual three decimal places.
  const decimals = Math.round(inches * 1000) === inches * 1000 ? 3 : 4;
  return "." + inches.toFixed(decimals).split(".")[1];
}

/** True for any wrapped (wound) string — anything that is not plain steel. */
export function isWound(winding: WindingType | undefined): boolean {
  return !!winding && winding !== "plain";
}

/**
 * Winding for a string at a given position, inferred from instrument category.
 *
 * Fallback only. `constructionForGauges` returns Aurora's real per-string
 * construction for any set we actually wind; this positional rule is used only
 * where nothing matches, and callers must not present its output as a verified
 * specification.
 */
export function inferWindingByPosition(
  category: string,
  index: number,
): WindingType {
  return windingForPosition(category, index);
}

/**
 * Format a full ordered gauge set, marking each string's construction:
 * "w" wound, "n" nylon, "nw" wound over a nylon core, nothing for plain steel.
 */
export function formatGaugeList(
  items: { gaugeValue: string | number; construction?: StringConstruction }[],
): string {
  return items
    .map((it) => {
      const gauge = formatGauge(it.gaugeValue);
      if (!gauge) return "";
      return gauge + (it.construction ? CONSTRUCTION_SUFFIX[it.construction] : "");
    })
    .filter((s) => s.length > 0)
    .join(" / ");
}

/** Split a pipe-separated catalog gauge string into its ordered values. */
export function splitGauges(individualGauges: string): string[] {
  return individualGauges
    .split("|")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Format a pipe-separated catalog gauge string into the full ordered set.
 *
 * Construction markers are added ONLY when the sequence matches one of Aurora's
 * own sets, so a set we do not wind is shown as bare gauges rather than being
 * labelled with a guessed plain/wound split. Nylon strings are never marked "w"
 * or shown as plain steel.
 */
export function formatGaugePipeList(
  individualGauges: string,
  category?: string,
): string {
  const values = splitGauges(individualGauges);
  const construction = category ? constructionForGauges(category, values) : null;
  return formatGaugeList(
    values.map((gaugeValue, index) => ({
      gaugeValue,
      construction: construction?.[index],
    })),
  );
}

/** True when the gauge list carries verified construction markers. */
export function hasVerifiedConstruction(
  individualGauges: string,
  category?: string,
): boolean {
  if (!category) return false;
  return constructionForGauges(category, splitGauges(individualGauges)) !== null;
}

/** Aurora's own name for an exact gauge sequence, or null if we don't wind it. */
export function auroraSetName(
  individualGauges: string,
  category?: string,
): string | null {
  if (!category) return null;
  return presetForGauges(category, splitGauges(individualGauges))?.name ?? null;
}

/** "6-string set" — makes clear the count is separate from the gauge numbers. */
export function stringCountLabel(count: number): string {
  return `${count}-string set`;
}

/** Map a display instrument name ("Electric Guitar") to a winding category. */
export function categoryFromInstrumentName(name: string): string {
  const n = name.toLowerCase();
  if (n.includes("bass")) return "bass";
  if (n.includes("acoustic")) return "acoustic";
  if (n.includes("classical")) return "classical";
  if (n.includes("ukulele")) return "ukulele";
  return "electric";
}
